import type { UserInfo } from '../auth/TokenManager';
import { ownerScope } from './JournalListService';
import { duringPaneAction } from './paneActivity';
import {
  CONNECTION_INACTIVE_MESSAGE,
  JOURNAL_BUSY_MESSAGE,
  signedInElsewhereMessage,
  SWITCHING_COMPANY_MESSAGE,
  TEMPLATE_INACTIVE_MESSAGE,
} from '../utils/journalMessages';

/** Who a request goes out as: the company and user in its token, and their names where known. */
export type JournalIdentity = Pick<UserInfo, 'userId'> & Partial<Pick<UserInfo, 'companyId' | 'email' | 'company'>>;

/** What the guard needs from the task pane. */
export interface JournalPaneHost {
  /**
   * Reads the Journal tab's rights and templates for whoever is signed in, and shows them only while
   * `current()` holds. Answers who it read them for, or null when it showed nothing.
   */
  readTab(current: () => boolean): Promise<UserInfo | null>;
  /** Who the add-in is signed in as now. */
  signedInAs(): Promise<JournalIdentity | null>;
  /** The pane drawn again for whoever is signed in now, its Journal tab emptied. Never recalculates. */
  redrawPane(): Promise<void>;
  /** Says why a click did nothing, where the user sees it. */
  refused(message: string): void;
  /** A passing notice, which leaves the Journal tab's own status as it is. */
  notice(message: string): void;
}

/**
 * Holds the Journal tab to the user and company its rights and templates were read for. Every open
 * workbook's pane shares the add-in's one sign-in, so a company switch in another workbook changes who
 * this pane sends as while its list still holds the other company's templates.
 */
export class JournalPaneGuard {
  private loadedFor: UserInfo | null = null;
  /** Bumped by every read and reset, so only the latest read is shown. */
  private generation = 0;
  private running = 0;
  private switching = 0;

  constructor(private readonly host: JournalPaneHost) {}

  /**
   * Reads the tab. Who it was read for is kept only when no reset or newer read came since. A read that
   * failed showed nothing, so the tab still holds what was read before, for whoever it was read for.
   */
  async load(): Promise<boolean> {
    const generation = ++this.generation;
    const current = (): boolean => generation === this.generation;
    const user = await this.host.readTab(current);
    if (!current() || user === null) return false;
    this.loadedFor = user;
    return true;
  }

  /** The tab holds nothing read; a read still running shows nothing. */
  forget(): void {
    this.generation++;
    this.loadedFor = null;
  }

  /** Whether requests carrying `sent`'s token go out as whoever the tab was read for. */
  holds(sent: JournalIdentity): boolean {
    const loaded = this.loadedFor;
    return loaded !== null && ownerScope(sent.companyId, sent.userId) === ownerScope(loaded.companyId, loaded.userId);
  }

  /** Whether a journal action is running in this pane. */
  get busy(): boolean {
    return this.running > 0;
  }

  /** Whether a company switch is running in this pane. */
  get switchingCompany(): boolean {
    return this.switching > 0;
  }

  /**
   * A company switch begins, and no journal action starts until the returned release is called: the
   * switch replaces the session, the Journal tab and the workbook's company under it, and its overlay
   * stops the mouse but not the keyboard.
   */
  switchStarting(): () => void {
    this.switching++;
    let held = true;
    return () => {
      if (!held) return;
      held = false;
      this.switching--;
    };
  }

  /**
   * Runs one journal action at a time, and none while the company is being switched. A click while
   * either runs is refused, never queued: the first may still change what the second would act on.
   */
  async run(action: () => Promise<void>): Promise<void> {
    if (this.switching > 0) {
      this.host.notice(SWITCHING_COMPANY_MESSAGE);
      return;
    }
    if (this.running > 0) {
      this.host.notice(JOURNAL_BUSY_MESSAGE);
      return;
    }
    this.running++;
    try {
      await duringPaneAction(action);
    } finally {
      this.running--;
    }
  }

  /**
   * Whether requests carrying `sent`'s token go out as the user and company the tab was read for. When
   * not, nothing may go out with the tab's templates: the pane is drawn and the tab read again for
   * whoever is signed in now, and the user is told why. With nothing read no template is picked, which
   * every action needs, so there is nothing to hold.
   */
  async sendsAsLoaded(sent: JournalIdentity): Promise<boolean> {
    const loaded = this.loadedFor;
    if (!loaded || this.holds(sent)) return true;
    this.forget();
    await this.host.redrawPane();
    await this.load();
    this.host.refused(signedInElsewhereMessage(this.loadedFor ?? sent, loaded));
    return false;
  }

  /** A template the Portal no longer offers: the list is read again and the user told. */
  templateInactive(): Promise<void> {
    return this.inactive(TEMPLATE_INACTIVE_MESSAGE);
  }

  /** The ERP connection the tab's rights named is no longer active: the tab is read again and the user told. */
  connectionInactive(): Promise<void> {
    return this.inactive(CONNECTION_INACTIVE_MESSAGE);
  }

  /**
   * A refusal of something the tab was read with. When the sign-in has moved to another company
   * meanwhile, that is what refused it, and what the user is told. When the tab cannot be read again,
   * the read already said so.
   */
  private async inactive(message: string): Promise<void> {
    const now = await this.host.signedInAs();
    if (now && !await this.sendsAsLoaded(now)) return;
    if (await this.load()) this.host.refused(message);
  }
}
